import PropTypes from "prop-types";
import React from "react";

export interface HashManagerProps {
  tabs: Record<string, string>;
  useHash?: boolean;
  defaultHash?: string;
  onHashChange: (hash: string | undefined) => void;
}

const propTypes = {
  tabs: PropTypes.shape({
    dummy: PropTypes.string,
  }).isRequired,
  useHash: PropTypes.bool,
  defaultHash: PropTypes.string,
  onHashChange: PropTypes.func.isRequired,
};

const defaultProps: Partial<HashManagerProps> = {
  tabs: {},
  useHash: false,
  defaultHash: undefined,
};

export const setHashToBrowser = (hash?: string) => {
  if (hash) {
    window.location.replace(`#/${hash}`);
  } else {
    window.location.hash = "";
  }
};

export const getHashFromBrowser = (): string => window.location.hash.substr(2);

export default class HashManager extends React.PureComponent<HashManagerProps> {
  static propTypes = propTypes;
  static defaultProps = defaultProps;

  static getNewHash = (
    tabs: Record<string, string>,
    defaultHash?: string
  ): string | undefined => {
    const keys = Object.keys(tabs);
    if (defaultHash && keys.includes(defaultHash)) {
      return defaultHash;
    }
    if (keys.length > 0) {
      return keys[0];
    }
    return undefined;
  };

  constructor(props: HashManagerProps) {
    super(props);
    if (props.useHash) {
      const hash = getHashFromBrowser();
      props.onHashChange(hash);
    }
  }

  componentDidMount() {
    const { useHash } = this.props;
    if (useHash) {
      const { tabs, defaultHash } = this.props;
      window.addEventListener("hashchange", this.handleHashChange);
      this.initializeHash(tabs, defaultHash);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: HashManagerProps) {
    const { tabs: oldTabs, useHash: oldUseHash } = this.props;
    const { tabs: newTabs, useHash: newUseHash } = nextProps;

    if (oldUseHash !== newUseHash) {
      if (newUseHash) {
        window.addEventListener("hashchange", this.handleHashChange);
        this.initializeHash(nextProps.tabs, nextProps.defaultHash);
      } else if (oldUseHash) {
        window.removeEventListener("hashchange", this.handleHashChange);
        this.terminateHash();
      }
    } else if (newUseHash && oldTabs !== newTabs) {
      this.initializeHash(nextProps.tabs, nextProps.defaultHash);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.handleHashChange);
  }

  private initializeHash = (
    tabs: Record<string, string>,
    defaultHash?: string
  ) => {
    const hash = getHashFromBrowser();
    if (!hash || !tabs[hash]) {
      const newHash = HashManager.getNewHash(tabs, defaultHash);
      if (newHash) {
        setHashToBrowser(newHash);
      }
    }
  };

  private terminateHash = () => {
    this.props.onHashChange(undefined);
  };

  private handleHashChange = () => {
    const { tabs } = this.props;
    const hash = getHashFromBrowser();
    const newHash = HashManager.getNewHash(tabs, hash);
    if (newHash !== hash && newHash) {
      setHashToBrowser(newHash);
    }
    this.props.onHashChange(newHash);
  };

  render() {
    return null;
  }
}
